import { useCallback, useEffect, useMemo, useState } from "react";
import { MetaTags } from "react-meta-tags";
import styled from "styled-components";

import Logo from "../components/Logo/Logo";
import Header from "../components/Header/Header";
import Navigation from "../components/Navigation/Navigation";
import Footer from "../components/Footer/Footer";
import API from "../api";
import { clearLocalStorage, setLocalStorage } from "../utils";
import { RESPONSE_STATUSES } from "../constants";
import AuthSlider from "../components/Slider/AuthSlider";
import ScrollMessage from "../components/ScrollMessage/ScrollMessage";
import PublicHeader from "../screens/Home/comp/Header";
import PublicFooter from "../screens/Home/comp/Footer";

/* =========================================================
   AUTH POPUP
========================================================= */

const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 20px;
  box-sizing: border-box;

  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  animation: fadeIn 0.25s ease-out forwards;

  &.fadeOut {
    animation: fadeOut 0.3s ease-in forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }
`;

const PopupBox = styled.div`
  position: relative;

  width: min(850px, 94vw);
  max-height: 90vh;

  overflow: hidden;

  background: #ffffff;
  border-radius: 22px;

  border: 1px solid rgba(255, 255, 255, 0.25);

  box-shadow:
    0 30px 80px rgba(0, 0, 0, 0.35),
    0 10px 30px rgba(0, 0, 0, 0.2);

  animation: slideUp 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(40px) scale(0.97);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 600px) {
    width: 94vw;
    border-radius: 16px;
  }
`;

const PopupContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 85vh;
    object-fit: contain;
  }
`;

const PopupCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;

  z-index: 10;

  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 50%;

  background: rgba(0, 0, 0, 0.65);
  color: #ffffff;

  font-size: 25px;
  line-height: 1;

  cursor: pointer;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.85);
    transform: scale(1.08);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`;

/* =========================================================
   AUTH PAGE
========================================================= */

export const AuthIntro = styled.div`
  margin-top: 12px;
  text-align: center;
  width: 100%;
`;

export const AuthTitle = styled.h2`
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  color: #d6dae3;

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

export const AuthSubtitle = styled.p`
  font-size: 14px;
  margin-top: 6px;
  color: #a5acb6;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StyledAuthContainer = styled.section`
  width: 100%;
  min-height: 100vh;

  position: fixed;
  inset: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  background: #1c1c1c;
`;

const StyledAuthContainerIn = styled.section`
  width: 100%;
  height: 100%;

  overflow-y: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  padding: 30px;
  box-sizing: border-box;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-height: 800px) {
    justify-content: flex-start;
  }

  @media (max-width: 1000px) {
    justify-content: flex-start;
  }
`;

const StyledAuthFormContainer = styled.section`
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  box-sizing: border-box;
`;

const StyledFormOuter = styled.section`
  width: auto;
  min-width: 400px;

  height: auto;

  margin: 0;
  padding: 0 20px 40px;

  box-sizing: border-box;

  @media (max-width: 500px) {
    min-width: 0;
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

/* =========================================================
   WIDTH AUTH HOC
========================================================= */

const widthAuthHOC = (Component, isLogin = false) => (props) => {
  const {
    appName = "App",
    appLogo = "/logo.png",
    bannerImages = [],
  } = props?.appData || {};

  const [showSliderPopup, setShowSliderPopup] = useState(false);
  const [closing, setClosing] = useState(false);

  /* ================= FILTER AUTH BANNERS ================= */

  const sliderImages = useMemo(() => {
    if (!Array.isArray(bannerImages)) {
      return [];
    }

    return bannerImages.filter(
      (item) => item?.type === "Authentication"
    );
  }, [bannerImages]);

  /* ================= SHOW POPUP ================= */

  useEffect(() => {
    if (!isLogin || sliderImages.length === 0) {
      return;
    }

    // const hasSeenPopup = localStorage.getItem("seenAuthPopup");

    // if (!hasSeenPopup) {
      setShowSliderPopup(true);
      localStorage.setItem("seenAuthPopup", "true");

      document.body.style.overflow = "hidden";
    //}

    return () => {
      document.body.style.overflow = "";
    };
  }, [isLogin, sliderImages.length]);

  /* ================= ESCAPE KEY ================= */

  useEffect(() => {
    if (!showSliderPopup) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePopup();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSliderPopup]);

  /* ================= CLOSE POPUP ================= */

  const closePopup = useCallback(() => {
    setClosing(true);

    setTimeout(() => {
      setShowSliderPopup(false);
      setClosing(false);

      document.body.style.overflow = "";
    }, 300);
  }, []);

  /* ================= RENDER ================= */

  return (
    <StyledAuthContainer>

      {/* ================= AUTH FORM ================= */}

      <StyledAuthFormContainer>
        <StyledAuthContainerIn>

          <Logo
            appName={appName}
            appLogo={appLogo}
            isAuth
          />

          {isLogin && (
            <AuthIntro>
              <AuthTitle>
                Welcome back!
              </AuthTitle>

              <AuthSubtitle>
                Secure access to your recharge dashboard
              </AuthSubtitle>
            </AuthIntro>
          )}

          <StyledFormOuter>
            <Component {...props} />
          </StyledFormOuter>

        </StyledAuthContainerIn>
      </StyledAuthFormContainer>

      {/* ================= BANNER POPUP ================= */}

      {isLogin &&
        showSliderPopup &&
        sliderImages.length > 0 && (
          <PopupOverlay
            className={closing ? "fadeOut" : ""}
            onClick={closePopup}
          >
            <PopupBox
              onClick={(event) => event.stopPropagation()}
            >

              <PopupCloseButton
                type="button"
                onClick={closePopup}
                aria-label="Close banner"
              >
                ×
              </PopupCloseButton>

              <PopupContent>
                <AuthSlider
                  images={sliderImages}
                  autoPlay={true}
                />
              </PopupContent>

            </PopupBox>
          </PopupOverlay>
        )}

    </StyledAuthContainer>
  );
};

/* =========================================================
   APP HOC
========================================================= */

const withAppHOC = (Component) => (props) => {
  const [profile, setProfile] = useState(null);
  const [scrollMessage, setScrollMessage] = useState("");
  const [showNav, setShowNav] = useState(false);

  const readScrollMessage = useCallback(async () => {
    const response = await API.get("/meta/scroll-message");

    const { status, data } = response.data;

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setScrollMessage(data);
    }
  }, []);

  const readProfile = useCallback(async () => {
    const response = await API.get("/authentication/profile");

    const { status, data } = response.data;

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setProfile(data);

      setLocalStorage(
        "userProfile",
        JSON.stringify(data)
      );
    }
  }, []);

  const onClickLogout = (e) => {
    e.preventDefault();

    API.delete("/authentication/logout");

    clearLocalStorage();

    window.location.href = "/login";
  };

  const onClickNavigationIcon = () => {
    setShowNav((showNav) => !showNav);
  };

  useEffect(() => {
    readProfile();
    readScrollMessage();
  }, [readProfile, readScrollMessage]);

  return (
    <>
      <Header
        fullName={
          profile?.firstName
            ? profile.firstName
            : "My Account"
        }
        {...profile}
        onClickLogout={onClickLogout}
        showNav={showNav}
        setShowNav={onClickNavigationIcon}
        {...props}
      />

      <Navigation
        {...props}
        showNav={showNav}
      />

      <ScrollMessage
        scrollMessage={scrollMessage}
      />

      <Component {...props} />

      <Footer {...props} />
    </>
  );
};

/* =========================================================
   PUBLIC HOC
========================================================= */

const withPublicHOC =
  (Component) =>
  ({ appData }) => {
    return (
      <>
        <MetaTags>
          <link
            href="https://fonts.googleapis.com/css?family=Poppins:200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i&display=swap"
            rel="stylesheet"
          />

          <link
            rel="stylesheet"
            href="css/bootstrap.min.css"
          />

          <link
            rel="stylesheet"
            href="css/normalize.css"
          />

          <link
            rel="stylesheet"
            href="css/style.css"
          />

          <link
            rel="stylesheet"
            href="css/responsive.css"
          />

          <link
            rel="stylesheet"
            href="css/color/color1.css"
          />

          <link
            rel="stylesheet"
            id="colors"
          />
        </MetaTags>

        <PublicHeader {...appData} />

        <Component {...appData} />

        <PublicFooter {...appData} />

        <script src="js/jquery.min.js"></script>
        <script src="js/bootstrap.min.js"></script>
        <script src="js/main.js"></script>
      </>
    );
  };

export {
  widthAuthHOC,
  withAppHOC,
  withPublicHOC,
};